import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ProjectIntegrationService } from '../../services/project-integration.service';
import { ProviderType } from '../../models/project-integration.model';
import { issueBrowseUrl } from '../../shared/issue-links';
import { mapTrackerError, TRACKER_ERROR_KEYS, TrackerError } from '../../shared/tracker-error';

/** Maps a provider status category to a semantic pill modifier. */
function categoryToClass(category: string | null | undefined): 'new' | 'progress' | 'done' | 'unknown' {
  switch ((category ?? '').toLowerCase()) {
    case 'new':
    case 'to do':
      return 'new';
    case 'indeterminate':
    case 'in progress':
      return 'progress';
    case 'done':
      return 'done';
    default:
      return 'unknown';
  }
}

interface LiveStatus {
  name: string;
  category: string;
}

/**
 * Provider-neutral display of a linked external issue (NEX-780): a deep link that
 * opens the correct provider URL (Jira `/browse/KEY`, Redmine `/issues/{id}`) plus
 * an on-demand live status pill loaded from the single-issue endpoint (no polling).
 *
 * Lives in `src/app/components/` because it is reused for requirements and test
 * results; a LIB overflow ticket tracks moving it into the component library.
 */
@Component({
  selector: 'app-linked-issue',
  imports: [TranslatePipe, RouterLink, MatIcon, MatProgressSpinner],
  templateUrl: './linked-issue.component.html',
  styleUrl: './linked-issue.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LinkedIssueComponent {
  private readonly integrationService = inject(ProjectIntegrationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly providerType = input.required<ProviderType>();
  readonly issueKey = input.required<string>();
  readonly projectId = input.required<string>();
  readonly integrationId = input.required<string>();
  readonly boardUrl = input<string | null>(null);
  readonly summary = input<string | null>(null);
  /** Hide the key/deep-link chip when the host already renders it; the status stays. */
  readonly showKey = input(true);

  readonly loading = signal(false);
  readonly errorKey = signal<string | null>(null);
  readonly trackerError = signal<TrackerError | null>(null);
  readonly trackerErrorKeys = TRACKER_ERROR_KEYS;
  private readonly liveStatus = signal<LiveStatus | null>(null);

  readonly browseUrl = computed(() => issueBrowseUrl(this.providerType(), this.issueKey(), this.boardUrl()));
  readonly status = computed(() => this.liveStatus());
  readonly statusClass = computed(() => categoryToClass(this.liveStatus()?.category));

  loadStatus(): void {
    if (this.loading()) return;
    this.loading.set(true);
    this.errorKey.set(null);
    this.trackerError.set(null);
    this.integrationService
      .getSingleIssue(this.projectId(), this.integrationId(), this.issueKey())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (issue) => {
          this.liveStatus.set({ name: issue.statusName, category: issue.statusCategory });
          this.loading.set(false);
        },
        error: (error: unknown) => {
          const tracker = mapTrackerError(error, this.boardUrl());
          this.trackerError.set(tracker);
          this.errorKey.set(tracker?.key ?? 'linkedIssue.statusError');
          this.loading.set(false);
        },
      });
  }
}
